/**
 * The indexable catalogue: search, filters, and the grid.
 *
 * The September redesign keeps photographs and comparison facts together.
 * Search and platform/region filters use server queries with indexable URLs.
 * Platform suggestions are observed values, not an invented catalog facet.
 * Filtered-empty and an empty catalog remain distinct states.
 */

import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { InternalLink } from "../components/InternalLink.js";
import { SlabFrame } from "../components/SlabFrame.js";
import { GarageShelf, ShopLight } from "../components/GarageScene.js";
import { PublicImage } from "../components/PublicImage.js";
import { Money, StatusChip } from "../components/primitives.js";
import { EmptyBlock, FilteredEmptyBlock, ResourceGate } from "../components/states.js";
import { useAnnounce } from "../components/Announcer.js";
import { CATALOG_PAGE_SIZE, catalogPath } from "../lib/coreRoutes.js";
import { availabilityLabel } from "../lib/labels.js";
import { parseItemList, type PublicItem } from "../lib/publicContract.js";
import { catalogHref, itemHref } from "../lib/routes.js";
import { useResource } from "../lib/useResource.js";

export function CatalogView({
  search,
  platform,
  region,
  offset
}: {
  search: string;
  platform: string;
  region: string;
  offset: number;
}): ReactNode {
  const { transport, navigate, preload } = useStorefront();
  const announce = useAnnounce();

  const path = catalogPath({ search, platform, region, limit: CATALOG_PAGE_SIZE, offset });
  const initial = preload !== null && preload.path === path ? (preload.items ?? null) : null;

  const resource = useResource<readonly PublicItem[]>(transport, {
    path,
    parse: parseItemList,
    requirement: "the public catalogue",
    initial
  });

  return (
    <div className="stack catalog">
      {/* A div, not a header: only the shell's masthead is the banner landmark. */}
      <div className="catalog-head hero">
        <ShopLight />
        <div className="hero-copy">
          <h1 className="sale-sign">
            <span className="sale-sign-main">Garage sale</span>{" "}
            <span className="sale-sign-sub">Graded games inside</span>
          </h1>
          <p className="hero-note">Every game is photographed. You get the exact copy you see.</p>
        </div>
        <GarageShelf />
      </div>

      {/*
        * Keyed on the applied query. A back/forward navigation changes the URL,
        * and the form has to follow it or the inputs start describing a page
        * that is no longer on screen. Remounting is how that stays a fact about
        * the URL rather than an effect that copies props into state.
        */}
      <CatalogFilters
        key={`${search}|${platform}|${region}`}
        onSubmit={(next) => {
          navigate(catalogHref(next));
        }}
        platform={platform}
        region={region}
        search={search}
        platforms={[...new Set((resource.value ?? []).map((item) => item.platform))].sort()}
      />

      <ResourceGate resource={resource} skeletonCards={6} subject="catalogue" waitingFor="the catalogue">
        {(items) => (
          <CatalogResults
            filtered={search.length > 0 || platform.length > 0 || region.length > 0}
            items={items}
            offset={offset}
            onAnnounce={announce}
            onClearFilters={() => {
              navigate(catalogHref({}));
            }}
            platform={platform}
            region={region}
            search={search}
          />
        )}
      </ResourceGate>
    </div>
  );
}

interface FilterDraft {
  readonly search: string;
  readonly platform: string;
  readonly region: string;
}

function CatalogFilters({
  search,
  platform,
  region,
  onSubmit,
  platforms
}: {
  search: string;
  platform: string;
  region: string;
  onSubmit: (next: FilterDraft) => void;
  platforms: readonly string[];
}): ReactNode {
  const [draft, setDraft] = useState<FilterDraft>({ search, platform, region });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit({ search: draft.search.trim(), platform: draft.platform.trim(), region: draft.region.trim() });
  };

  return (
    <form aria-labelledby="filters-title" className="filters" onSubmit={submit}>
      <h2 className="u-visually-hidden" id="filters-title">
        Search and filter the catalogue
      </h2>
      <div className="field filters-search">
        <label className="t-label" htmlFor="filter-search">
          Search titles and SKUs
        </label>
        <input
          autoComplete="off"
          id="filter-search"
          maxLength={100}
          name="q"
          placeholder="A game title or SKU"
          onChange={(event) => {
            setDraft((current) => ({ ...current, search: event.target.value }));
          }}
          type="search"
          value={draft.search}
        />
      </div>
      <div className="field filters-platform">
        <label className="t-label" htmlFor="filter-platform">Platform</label>
        <input id="filter-platform" list="catalog-platforms" name="platform" maxLength={80}
          placeholder="All platforms" value={draft.platform}
          onChange={(event) => { setDraft((current) => ({ ...current, platform: event.target.value })); }} />
        <datalist id="catalog-platforms">{platforms.map((value) => <option key={value} value={value} />)}</datalist>
      </div>
      <button className="btn btn--primary filters-submit" type="submit">Search</button>
      <details className="filters-more" open={platform.length > 0 || region.length > 0}>
        <summary>Region &amp; filter options</summary>
        <div className="filters-extra">
          <p className="t-body-s t-secondary">Platform suggestions come from these results. You can enter another platform to search the catalogue.</p>
          <div className="field">
            <label className="t-label" htmlFor="filter-region">
              Region
            </label>
            <input
              autoComplete="off"
              id="filter-region"
              maxLength={40}
              name="region"
              placeholder="e.g. NTSC-U"
              onChange={(event) => {
                setDraft((current) => ({ ...current, region: event.target.value }));
              }}
              type="text"
              value={draft.region}
            />
          </div>
          <div className="filters-actions">
            <button className="btn btn--primary" type="submit">
              Apply filters
            </button>
            <InternalLink className="btn btn--quiet" href="/catalog">
              Clear filters
            </InternalLink>
          </div>
        </div>
      </details>
      {search.length > 0 || platform.length > 0 || region.length > 0 ? (
        <div aria-label="Applied filters" className="filter-tags">
          {search.length > 0 ? <InternalLink href={catalogHref({ platform, region })}>Search: {search} <span aria-hidden="true">×</span><span className="u-visually-hidden"> Remove search</span></InternalLink> : null}
          {platform.length > 0 ? <InternalLink href={catalogHref({ search, region })}>Platform: {platform} <span aria-hidden="true">×</span><span className="u-visually-hidden"> Remove platform filter</span></InternalLink> : null}
          {region.length > 0 ? <InternalLink href={catalogHref({ search, platform })}>Region: {region} <span aria-hidden="true">×</span><span className="u-visually-hidden"> Remove region filter</span></InternalLink> : null}
        </div>
      ) : null}
    </form>
  );
}

function CatalogResults({
  items,
  filtered,
  search,
  platform,
  region,
  offset,
  onClearFilters,
  onAnnounce
}: {
  items: readonly PublicItem[];
  filtered: boolean;
  search: string;
  platform: string;
  region: string;
  offset: number;
  onClearFilters: () => void;
  onAnnounce: (message: string) => void;
}): ReactNode {
  const count = items.length;

  // 9.4: the result count is announced politely once per result set, so a
  // keyboard user who submitted the form hears what happened without moving.
  const announceResults = useCallback(() => {
    onAnnounce(
      count === 0
        ? "No items match these filters."
        : `${String(count)} item${count === 1 ? "" : "s"} listed.`
    );
  }, [count, onAnnounce]);

  useEffect(() => {
    announceResults();
  }, [announceResults]);

  if (count === 0) {
    if (offset > 0) {
      return (
        <EmptyBlock
          statement="You have reached the end of these results."
          action={<InternalLink className="btn btn--secondary" href={catalogHref({ search, platform, region, offset: Math.max(0, offset - CATALOG_PAGE_SIZE) })}>Previous page</InternalLink>}
        />
      );
    }
    return filtered ? (
      <FilteredEmptyBlock onClear={onClearFilters} />
    ) : (
      <EmptyBlock
        action={
          <a className="btn btn--secondary" href="/help">
            Ask what is coming
          </a>
        }
        statement="Nothing is listed for sale on this site right now."
      />
    );
  }

  const previous = Math.max(0, offset - CATALOG_PAGE_SIZE);
  const hasPrevious = offset > 0;
  const hasNext = count === CATALOG_PAGE_SIZE;

  return (
    <div className="stack">
      <p aria-live="off" className="t-body-s t-secondary">
        {/* 13.2: a count is never shown as `0` while loading; this renders only
            once a result set exists. */}
        Showing <span className="t-id">{count}</span> item{count === 1 ? "" : "s"}
        {offset > 0 ? (
          <>
            {" "}
            from position <span className="t-id">{offset + 1}</span>
          </>
        ) : null}
        .
      </p>
      <ul className="catalog-grid">
        {items.map((item) => (
          <li key={item.physical_item_id}>
            <CatalogCard item={item} />
          </li>
        ))}
      </ul>
      {hasPrevious || hasNext ? (
        <nav aria-label="Catalogue pages" className="pager">
          {hasPrevious ? (
            <InternalLink
              className="btn btn--secondary"
              href={catalogHref({ search, platform, region, offset: previous })}
            >
              Previous page
            </InternalLink>
          ) : null}
          {hasNext ? (
            <InternalLink
              className="btn btn--secondary"
              href={catalogHref({ search, platform, region, offset: offset + CATALOG_PAGE_SIZE })}
            >
              Next page
            </InternalLink>
          ) : null}
        </nav>
      ) : null}
    </div>
  );
}

export function CatalogCard({ item }: { item: PublicItem }): ReactNode {
  const availability = availabilityLabel(item.availability, item.checkout_eligible);
  const cover = item.public_derivatives[0];
  const inspectionLabel = `Inspect ${item.title}, ${item.grading_company !== undefined && item.grade !== undefined ? `${item.grading_company} ${item.grade}, ` : ""}SKU ${item.sku}`;
  return (
    <article className="card">
      <InternalLink ariaLabel={inspectionLabel} className="card-image-link" href={itemHref(item.physical_item_id)}>
        <SlabFrame
          certNumber={item.cert_number}
          editionVariant={item.edition_variant}
          gradingCompany={item.grading_company}
          grade={item.grade}
          platform={item.platform}
          size="md"
          sku={item.sku}
        >
          {cover === undefined ? (
            <p className="t-body-s t-secondary card-nomedia">
              No approved photograph has been published for this copy yet.
            </p>
          ) : (
            <PublicImage
              retry={false}
              alt={`Approved photograph of ${item.title}, SKU ${item.sku}`}
              className="card-media"
              loading="lazy"
              src={cover}
            />
          )}
        </SlabFrame>
      </InternalLink>
      <div className="card-body">
        <h2 className="card-title t-body-l">
          <InternalLink href={itemHref(item.physical_item_id)}>{item.title}</InternalLink>
        </h2>
        <p className="t-body-s t-secondary">
          {item.platform} · {item.region}
        </p>
        <p className="card-grade">{item.grading_company === undefined ? "Ungraded copy" : `${item.grading_company} ${item.grade ?? "Grade not published"}`}</p>
        <p className="card-price">
          <Money minor={item.price_minor} />
          <StatusChip label={availability.label} tone={availability.tone} />
        </p>
      </div>
    </article>
  );
}
